import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Search, Calendar, Clock, ArrowRight, Tag, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PageHeader from '@/components/common/PageHeader';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  'All',
  'Industry Trends',
  'Canada Immigration',
  'Australia Immigration',
  'USA Immigration',
  'UK Immigration',
  'Europe Immigration',
  'UAE Immigration',
  'Healthcare Careers',
  'Application Tips',
  'Comparison Guides',
];

const BlogIndexPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    filterBlogs();
  }, [selectedCategory, searchQuery, blogs]);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blogs`);
      setBlogs(response.data);
      setFilteredBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBlogs = () => {
    let filtered = blogs;

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(blog => blog.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        blog =>
          blog.title.toLowerCase().includes(query) ||
          blog.excerpt.toLowerCase().includes(query) ||
          blog.category.toLowerCase().includes(query)
      );
    }

    setFilteredBlogs(filtered);
  };

  return (
    <div data-testid="blog-index-page">
      <Helmet defer={false}>
        <title>Immigration & Recruitment Blog | Zafco HR — Latest Visa & PR Updates</title>
        <meta
          name="description"
          content="Expert insights on Canada, Australia, UK, Germany & UAE immigration. Weekly visa policy updates, points calculators, application tips & comparison guides from Zafco HR."
        />
        <link rel="canonical" href="https://zafco.in/blogs" />
        <meta property="og:title" content="Immigration & Recruitment Blog | Zafco HR" />
        <meta property="og:url" content="https://zafco.in/blogs" />
        <meta property="og:type" content="website" />
      </Helmet>

      <PageHeader 
        title="Overseas Recruitment Insights & Updates"
        subtitle="Stay informed with the latest trends, tips, and news in global job placement and immigration"
        breadcrumbs={[{ label: 'Blogs' }]}
      />

      <section className="py-20">
        <div className="container mx-auto px-6">
          {/* Search & Filter */}
          <motion.div
            className="flex flex-col lg:flex-row gap-6 mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-white border-gray-200"
                data-testid="blog-search"
              />
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.slice(0, 6).map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category 
                    ? 'bg-zafco-blue text-white' 
                    : 'border-gray-200 hover:border-zafco-blue hover:text-zafco-blue'
                  }
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category}
                </Button>
              ))}
            </div>
          </motion.div>

          {/* Blog Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
                  <div className="h-48 bg-gray-200" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-24" />
                    <div className="h-6 bg-gray-200 rounded" />
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredBlogs.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog, index) => (
                <motion.article
                  key={blog.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`blog-card-${blog.slug}`}
                >
                  <Link to={`/blogs/${blog.slug}`}>
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <span className="absolute top-4 left-4 px-3 py-1 bg-gradient-to-r from-zafco-gold to-zafco-orange text-white text-xs font-semibold rounded-full">
                        {blog.category}
                      </span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {blog.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {blog.read_time}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-zafco-blue font-montserrat mb-3 line-clamp-2 group-hover:text-zafco-teal transition-colors">
                        {blog.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                        {blog.excerpt}
                      </p>
                      <span className="inline-flex items-center text-zafco-teal font-semibold text-sm group-hover:gap-2 transition-all">
                        Read More <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <h3 className="text-xl font-semibold text-gray-600 mb-4">No articles found</h3>
              <p className="text-gray-500 mb-6">Try adjusting your search or filter criteria</p>
              <Button onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}>
                Clear Filters
              </Button>
            </div>
          )}

          {/* Newsletter CTA */}
          <motion.div
            className="mt-20 bg-gradient-to-br from-zafco-blue to-zafco-teal rounded-2xl p-8 md:p-12 text-white text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl md:text-3xl font-bold font-montserrat mb-4">
              Stay Updated with Immigration News
            </h3>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Get the latest overseas job opportunities, visa updates, and expert tips delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/60 h-12"
              />
              <Button className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-8 h-12">
                Subscribe
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogIndexPage;
